import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Stack, TextField } from '@mui/material'

import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import {
  getMotivationKeyError,
  MotivationKeyError,
  motivationLanguages,
  MotivationLocalizationFormValues,
  MotivationLocalizationRow,
  toMotivationLocalizationFormValues,
} from '../../helpers/motivationLocalizationHelpers'

type MotivationLocalizationEditDialogProps = {
  open: boolean
  row?: MotivationLocalizationRow
  rows: MotivationLocalizationRow[]
  isSaving?: boolean
  onClose: () => void
  onSave: (values: MotivationLocalizationFormValues) => void
}

const keyErrorKeys: Record<MotivationKeyError, string> = {
  required: 'internal-service-management:motivations_key_required',
  invalid: 'internal-service-management:motivations_key_invalid',
  taken: 'internal-service-management:motivations_key_taken',
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const MotivationLocalizationEditDialog: FC<MotivationLocalizationEditDialogProps> = ({ open, row, rows, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<MotivationLocalizationFormValues>({
    mode: 'onChange',
    defaultValues: toMotivationLocalizationFormValues(row),
  })

  useEffect(() => {
    if (open) reset(toMotivationLocalizationFormValues(row))
  }, [open, row, reset])

  const submit = handleSubmit((values) => onSave(values))

  return (
    <InternalFormDialog
      open={open}
      title={
        row ? t('internal-service-management:motivations_edit_localization', { key: row.key }) : t('internal-service-management:motivations_add_localization')
      }
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="key"
          control={control}
          rules={{
            validate: (key) => {
              if (row) return true
              const error = getMotivationKeyError(key, rows)
              return error ? t(keyErrorKeys[error]) : true
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              label={t('internal-service-management:motivations_key')}
              disabled={!!row}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || (row ? t('internal-service-management:motivations_key_read_only') : undefined)}
            />
          )}
        />
        <Controller
          name="names"
          control={control}
          rules={{ validate: (names) => !!names.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-service-management:motivations_name')}
              dialogTitle={t('internal-service-management:motivations_name')}
              selectedLanguages={motivationLanguages}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
